import { useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type Customer = {
  id: number;
  custNum: string;
  name: string;
  contactName: string | null;
  city: string | null;
  province: string | null;
  address: string | null;
  postalCode: string | null;
  email: string | null;
  phone: string | null;
  isWalkIn: boolean;
  isActive: boolean;
  sharedAccount?: boolean;
};

/** Coordonnées OBLIGATOIRES d'un client avant de démarrer une commande. */
export type ClientRequis = {
  name: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
};

export type OrderLine = {
  id: number;
  productId: number;
  lineCode: string;
  partNumber: string;
  description: string;
  uom: string;
  unitPrice: string;
  qty: number;
  discountAmount: string;
  lineTotal: string;
  promoLabel: string | null;
};

export type Order = {
  id: number;
  number: string | null;
  status: 'DRAFT' | 'SUBMITTED' | 'CANCELLED';
  poNumber: string | null;
  signerName: string | null;
  notes: string | null;
  subtotal: string;
  discountTotal: string;
  total: string;
  submittedAt: string | null;
  customer: Customer;
  lines: OrderLine[];
};

export type OrderSummary = {
  id: number;
  number: string | null;
  status: Order['status'];
  total: string;
  createdAt: string;
  signerName: string | null;
  customerName: string;
  custNum: string;
};

/**
 * Identifiant du panier en cours, gardé en localStorage.
 *
 * Le panier fait autorité côté serveur ; ce n'est qu'un fil d'Ariane. Sur un
 * iPad qui se verrouille, une PWA que Safari décharge de la mémoire ou un
 * rechargement accidentel, il ramène le rep sur SA commande plutôt que sur un
 * écran vide — sans jamais servir de source de vérité pour les montants.
 */
const CLE_PANIER = 'supershow.panier';

export function memoriserPanier(orderId: number) {
  try {
    localStorage.setItem(CLE_PANIER, String(orderId));
  } catch {
    // Mode privé ou stockage plein : on continue sans filet, ce n'est pas bloquant.
  }
}

export function panierMemorise(): number | null {
  try {
    const v = localStorage.getItem(CLE_PANIER);
    const n = v ? Number(v) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function oublierPanier() {
  try {
    localStorage.removeItem(CLE_PANIER);
  } catch {
    /* sans conséquence */
  }
}

/** Mémorise le panier courant tant qu'il est un brouillon. */
export function useSouvenirDuPanier(order: Order | undefined) {
  useEffect(() => {
    if (!order) return;
    if (order.status === 'DRAFT') memoriserPanier(order.id);
    else oublierPanier();
  }, [order?.id, order?.status]);
}

// --- Requêtes --------------------------------------------------------------

export function useCustomers(
  q: string,
  enabled = true,
  limit = 30,
  offset = 0,
  walkIn = false,
  includeInactive = false,
) {
  return useQuery<{ customers: Customer[]; total: number; offset: number }>({
    queryKey: ['customers', q, limit, offset, walkIn, includeInactive],
    queryFn: () =>
      api(
        `/api/customers?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}` +
          `${walkIn ? '&walkIn=1' : ''}${includeInactive ? '&includeInactive=1' : ''}`,
      ),
    staleTime: 60_000,
    enabled,
  });
}

export function useOrder(id: number | null) {
  return useQuery<{ order: Order }>({
    queryKey: ['order', id],
    queryFn: () => api(`/api/orders/${id}`),
    enabled: id !== null,
  });
}

export type OrderStatus = 'DRAFT' | 'SUBMITTED' | 'CANCELLED';
export type OrderCounts = { total: number; DRAFT: number; SUBMITTED: number; CANCELLED: number };

export function useOrders(params?: {
  q?: string;
  status?: OrderStatus;
  limit?: number;
  offset?: number;
}) {
  const q = params?.q ?? '';
  const status = params?.status;
  const limit = params?.limit ?? 100;
  const offset = params?.offset ?? 0;
  return useQuery<{
    orders: OrderSummary[];
    total: number;
    counts: OrderCounts;
    submittedCount: number;
    submittedTotal: string;
  }>({
    queryKey: ['orders', q, status ?? '', limit, offset],
    queryFn: () =>
      api(
        `/api/orders?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}${status ? `&status=${status}` : ''}`,
      ),
    // Garde l'affichage précédent pendant une nouvelle recherche/pagination.
    placeholderData: keepPreviousData,
  });
}

/**
 * Toutes les mutations du panier renvoient la commande entière recalculée par
 * le serveur, qu'on réinjecte directement dans le cache : les totaux affichés
 * sont toujours ceux du serveur, jamais un calcul refait côté client.
 */
function useMutationPanier(orderId: number) {
  const qc = useQueryClient();
  return {
    onSuccess: (data: { order: Order }) => {
      qc.setQueryData(['order', orderId], data);
      void qc.invalidateQueries({ queryKey: ['orders'] });
      // La progression cadeaux dépend du contenu du panier : à rafraîchir.
      void qc.invalidateQueries({ queryKey: ['order-rewards', orderId] });
    },
  };
}

export type RewardGiftProgress = { giftName: string; value: string; reached: boolean };
export type RewardProgress = {
  groupId: number;
  groupName: string;
  cumulative: string;
  gifts: RewardGiftProgress[];
  next: { giftName: string; value: string; remaining: string } | null;
};

/** Progression cadeaux d'une commande (montant restant avant le prochain cadeau). */
export function useOrderRewards(orderId: number) {
  return useQuery<{ groups: RewardProgress[] }>({
    queryKey: ['order-rewards', orderId],
    queryFn: () => api(`/api/orders/${orderId}/rewards`),
  });
}

export function useAjouterAuPanier(orderId: number) {
  const commun = useMutationPanier(orderId);
  return useMutation({
    mutationFn: (body: { productId: number; qty?: number }) =>
      api<{ order: Order }>(`/api/orders/${orderId}/lines`, {
        method: 'POST',
        body: JSON.stringify({ qty: 1, ...body }),
      }),
    ...commun,
  });
}

export function useChangerQuantite(orderId: number) {
  const commun = useMutationPanier(orderId);
  return useMutation({
    mutationFn: ({ lineId, qty }: { lineId: number; qty: number }) =>
      api<{ order: Order }>(`/api/orders/${orderId}/lines/${lineId}`, {
        method: 'PATCH',
        body: JSON.stringify({ qty }),
      }),
    ...commun,
  });
}

export function useEnregistrerEntete(orderId: number) {
  const commun = useMutationPanier(orderId);
  return useMutation({
    mutationFn: (body: {
      poNumber?: string | null;
      signerName?: string | null;
      notes?: string | null;
    }) =>
      api<{ order: Order }>(`/api/orders/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    ...commun,
  });
}

export function useSoumettre(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ order: Order; number: string }>(`/api/orders/${orderId}/submit`, { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(['order', orderId], { order: data.order });
      void qc.invalidateQueries({ queryKey: ['orders'] });
      oublierPanier();
    },
  });
}

/**
 * Annulation d'une commande (route `cancel`, statut → CANCELLED). Un brouillon
 * abandonné qu'on retire ainsi de la liste plutôt que de le laisser traîner. Le
 * serveur n'autorise que le rep propriétaire ou un admin ; une commande soumise
 * reste réservée à l'admin.
 */
export function useAnnulerCommande(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ order: Order }>(`/api/orders/${orderId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['order', orderId] });
      oublierPanier();
    },
  });
}

/**
 * Réouvre une commande annulée en brouillon (admin). Le serveur remet le statut à
 * DRAFT et efface le numéro ; on réinjecte la commande dans le cache, ce qui fait
 * rebasculer l'écran vers l'éditeur de commande.
 */
export function useReouvrirCommande(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ order: Order }>(`/api/orders/${orderId}/reopen`, { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(['order', orderId], data);
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Suppression permanente d'une commande (admin ; brouillon ou annulée seulement). */
export function useSupprimerCommande(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>(`/api/orders/${orderId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Édition d'entête par l'admin (signataire, PO, notes) — quel que soit le statut. */
export function useModifierEntete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; signerName: string; poNumber: string; notes: string }) =>
      api<{ order: Order }>(`/api/orders/${id}/entete`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      qc.setQueryData(['order', data.order.id], data);
    },
  });
}

export function useCreerCommande() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: number) =>
      api<{ order: Order }>('/api/orders', { method: 'POST', body: JSON.stringify({ customerId }) }),
    onSuccess: (data) => {
      qc.setQueryData(['order', data.order.id], data);
      memoriserPanier(data.order.id);
    },
  });
}

/** URL de téléchargement du PDF d'une commande (route serveur, session cookie). */
export function urlPdf(orderId: number): string {
  return `/api/orders/${orderId}/pdf`;
}

export type ClientOrderLine = {
  lineCode: string;
  partNumber: string;
  description: string;
  qty: number;
  unitPrice: string;
  lineTotal: string;
};
export type ClientOrder = {
  id: number;
  number: string | null;
  submittedAt: string | null;
  total: string;
  repName: string;
  lines: ClientOrderLine[];
};

/**
 * Historique des commandes soumises d'un client (tous reps), avec le détail des
 * articles. Réservé admin / représentant super (voir le endpoint serveur).
 */
export function useClientOrders(customerId: number | null) {
  return useQuery<{ orders: ClientOrder[] }>({
    queryKey: ['client-orders', customerId],
    queryFn: () => api(`/api/customers/${customerId}/orders`),
    enabled: customerId !== null,
  });
}

/** Tous les courriels connus d'un client — pour le choix à la révision de commande. */
export function useCustomerEmails(customerId: number) {
  return useQuery<{ emails: string[] }>({
    queryKey: ['customer-emails', customerId],
    queryFn: () => api(`/api/customers/${customerId}/emails`),
    staleTime: 60_000,
  });
}

/** Envoie la commande au client par courriel. `to` facultatif (défaut : courriel du client). */
export function useEnvoyerCourriel(orderId: number) {
  return useMutation({
    mutationFn: (to?: string) =>
      api<{ ok: true; to: string; messageId: string }>(`/api/orders/${orderId}/email`, {
        method: 'POST',
        body: JSON.stringify(to ? { to } : {}),
      }),
  });
}

export function useCreerWalkIn() {
  return useMutation({
    mutationFn: (body: ClientRequis & { email?: string }) =>
      api<{ customer: Customer }>('/api/customers/walk-in', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

/** Complète les coordonnées obligatoires d'un client existant (avant commande). */
export function useMettreAJourClient() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & ClientRequis & { email?: string }) =>
      api<{ customer: Customer }>(`/api/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  });
}

/**
 * Création d'un client depuis l'espace admin (mêmes fiches walk-in : préfixe WI-,
 * à rapprocher de l'ERP après le show). Rafraîchit la liste des clients au succès.
 */
export function useCreerClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      email?: string;
      phone?: string;
      address?: string;
      city?: string;
      province?: string;
      postalCode?: string;
    }) =>
      api<{ customer: Customer }>('/api/customers/walk-in', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/** Gestion admin d'un nouveau client (walk-in) : modifier ses champs et/ou l'activer/désactiver. */
export function useGererClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      name?: string;
      phone?: string;
      address?: string;
      city?: string;
      province?: string;
      postalCode?: string;
      email?: string;
      isActive?: boolean;
    }) =>
      api<{ customer: Customer }>(`/api/customers/${id}/manage`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/** Suppression admin d'un nouveau client (walk-in), refusée s'il a des commandes. */
export function useSupprimerClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<{ ok: true }>(`/api/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
